import * as Sentry from "@sentry/react";
import { UseQueryResult } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useFinalize } from "@krea/common/store/banking-data/hooks/mutations/useFinalize";
import { useInitialize } from "@krea/common/store/banking-data/hooks/mutations/useInitialize";
import { useInstitutionsQuery } from "@krea/common/store/banking-data/hooks/queries/useInstitutionsQuery";
import { IInstitution } from "@krea/common/store/banking-data/types";
import { isMobile } from "@krea/common/utils";

export function useGCBankingData({
  resourceId,
  resourceType,
  onSuccess,
}: {
  resourceId: string;
  resourceType: string;
  onSuccess?: () => void;
}): {
  institutionsQuery: UseQueryResult<IInstitution[]>;
  initialize: (institutionId: string) => Promise<void>;
  reset: (resetIsError?: boolean) => void;
  isError?: boolean;
  isCancelled?: boolean;
} {
  /*
   * GoCardless PSD2 flow
   *
   * */
  const { i18n } = useTranslation();
  const flowCheckerTimer = useRef<number | null>(null);
  const [externalFlowWindow, setExternalFlowWindow] = useState<Window | null>(
    null,
  );
  const [isError, setIsError] = useState<boolean>(false);
  const [isCancelled, setIsCancelled] = useState<boolean>(false);
  const institutionsQuery = useInstitutionsQuery({
    resourceId,
    resourceType,
  });

  const initializeMutation = useInitialize();
  const finalizeMutation = useFinalize({ resourceId, resourceType });

  const initialize = async (institutionId: string) => {
    let externalFlowWindow = null;

    const useNewTab = isMobile(); // Use this if we want to control new/small window popup for oauth.
    //const useNewTab = true;

    if (useNewTab) {
      // If mobile "pre-open" the window is needed
      externalFlowWindow = window.open("about:blank", "_blank");
    } else {
      const windowHeight = 950;
      const windowWidth = 600;
      const features = `
        location=no,
        menubar=no,
        status=no,
        titlebar=no,
        toolbar=no,
        width=${windowWidth},
        height=${windowHeight},
        top=${(window.innerHeight - windowHeight) / 2},
        left=${(window.innerWidth - windowWidth) / 2}`;

      externalFlowWindow = window.open("about:blank", "popup", features);
    }

    try {
      const data = await initializeMutation.mutateAsync({
        institutionId,
        redirectUrl: `${window.location.origin}/redirect-return-page`,
        locale: i18n.language,
      });
      // const response = await axios.post(
      //   `/api/v1/banking-data/sessions/initialize`,
      //   {
      //     institutionId,
      //     redirectUrl: `${window.location.origin}/redirect-return-page`,
      //     locale: i18n.language,
      //   },
      // );

      //const externalFlowWindow = window.open(response.data.auth_url, '_blank').focus()
      const url = data.link;

      if (!url) {
        throw new Error("auth_url was null in react state. Should never be");
      }

      if (externalFlowWindow) {
        // If mobile the window opened pre http fetch (mobile browser security reasons)
        externalFlowWindow.location.href = url;
      } else {
        const features = `
        location=no,
        resizable=no,
        menubar=no,
        status=no,
        titlebar=no,
        toolbar=no,
        width=${400},
        height=${650},
        top=${(window.innerHeight - 400) / 2},
        left=${(window.innerWidth - 400) / 2}`;

        externalFlowWindow = window.open(url, "popup", features);
      }

      setExternalFlowWindow(externalFlowWindow);
    } catch (e) {
      Sentry.captureException(e);

      if (externalFlowWindow && !externalFlowWindow.closed) {
        externalFlowWindow.close();
      }

      setExternalFlowWindow(null);
      setIsError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  const flowChecker = () => {
    flowCheckerTimer.current = window.setInterval(async () => {
      if (externalFlowWindow) {
        if (!externalFlowWindow.closed) {
          try {
            // Yay we are redirected back to our domain in the external window
            if (externalFlowWindow.location.origin === window.location.origin) {
              const params = new URLSearchParams(
                externalFlowWindow.location.search,
              );
              console.log("params: ", params.toString());

              // ref is the "flow" (requisition id) id we got from the oauth flow
              const ref = params.get("ref");

              // error string we got from oauth flow (on non-success)
              let error = params.get("error");

              if (ref === "cancel") {
                error = ref;
              }

              if (externalFlowWindow) {
                externalFlowWindow.close();
              }

              if (error) {
                const userCancellationErrors = ["UserCancelledSession"];

                if (userCancellationErrors.includes(error)) {
                  setIsCancelled(true);
                } else {
                  setIsError(true);
                }
              } else if (ref) {
                // Success from external window
                //finalize(ref);
                setExternalFlowWindow(null);
                finalizeMutation.mutate(
                  { requisitionId: ref },
                  {
                    onSuccess: () => {
                      if (onSuccess) {
                        onSuccess();
                      }
                      reset(true);
                    },
                    onError: (e) => {
                      console.error(
                        "Error on banking data finalize: ",
                        JSON.stringify(e),
                      );
                      Sentry.captureException(e);
                      setIsError(true);
                    },
                  },
                );
              } else {
                console.log("Unknown: ", error);
                setExternalFlowWindow(null);
                setIsError(true);
              }

              if (flowCheckerTimer.current) {
                clearInterval(flowCheckerTimer.current);
              }

              flowCheckerTimer.current = null;
            }
          } catch (e) {
            if (e instanceof DOMException) {
              // This mean that the the external window is still on oath provider's domain.
              // So CORS will force us in here.
              // We're not allowed to access "externalFlowWindow.location") if external window is
              // not on same domain as us.
              // As long as we go in here the "oath provider" user flow can be considered "pending"
            } else {
              console.error("Error in GC banking data flowChecker: ", e);
              Sentry.captureException(e);
              throw e;
            }
          }
        } else {
          // Window was closed by user
          if (flowCheckerTimer.current) {
            clearInterval(flowCheckerTimer.current);
          }
          setIsCancelled(true);
          flowCheckerTimer.current = null;
          setExternalFlowWindow(null);
        }
      } else {
        Sentry.captureMessage(
          "External flow window is null, should not happen",
        );
        setIsError(true);
      }
    }, 500);
  };

  useEffect(() => {
    if (externalFlowWindow) {
      flowChecker();
      externalFlowWindow.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalFlowWindow]);

  useEffect(() => {
    return () => {
      if (flowCheckerTimer.current) {
        clearInterval(flowCheckerTimer.current);
      }
    };
  }, []);

  const reset = (resetIsError: boolean = false) => {
    setExternalFlowWindow(null);

    if (resetIsError) {
      setIsError(false);
    }
    setIsCancelled(false);
    initializeMutation.reset();
    finalizeMutation.reset();
  };

  return {
    institutionsQuery,
    initialize,
    reset,
    isError:
      institutionsQuery.isError ||
      initializeMutation.isError ||
      finalizeMutation.isError ||
      isError,
    isCancelled,
  };
}
